import React, {FC, forwardRef, useCallback, useMemo} from 'react';

import {Select, SelectOption} from '@gravity-ui/uikit';
import QuestionLayout, {QuestionLayoutType} from 'ui/components/QuestionLayout';
import i18n from 'ui/i18n';

import VisuallyHidden from '../VisuallyHidden';

import {cnDropdownQuestion} from './DropdownQuestion.constants';
import {DropdownQuestionProps} from './DropdownQuestion.types';
import {renderOption} from './DropdownQuestion.utils';

import './DropdownQuestion.scss';

const iframe = window.YC?.iframe;

const DefaultOption = () => {
    return (
        <>
            <span aria-hidden="true">–</span>
            <VisuallyHidden>{i18n('survey-page', 'sr-default-option')}</VisuallyHidden>
        </>
    );
};

const DropdownQuestion: FC<DropdownQuestionProps> = forwardRef<
    HTMLButtonElement,
    DropdownQuestionProps
>(function DropdownQuestion(
    {
        size = 'l',
        view = 'normal',
        items,
        placeholder = <DefaultOption />,
        error,
        suggested = false,
        showFirst = false,
        required,
        onUpdate: onUpdateBase,
        ...props
    },
    ref,
) {
    const options = useMemo(() => {
        const result = items.map<SelectOption>((item) => ({
            content: item.label,
            value: item.id,
        }));
        if (!showFirst && !required) {
            result.unshift({content: <DefaultOption />, value: ''});
        }
        return result;
    }, [items, required, showFirst]);

    const onUpdate = useCallback(
        (value: string[]) => {
            if (onUpdateBase) {
                if (value.filter(Boolean).length === 0) {
                    onUpdateBase([]);
                } else {
                    onUpdateBase(value);
                }
            }
        },
        [onUpdateBase],
    );

    return (
        <QuestionLayout
            {...props}
            required={required}
            type={QuestionLayoutType.Column}
            className={cnDropdownQuestion('', [props.className])}
            error={error}
            control={
                <Select
                    {...props}
                    ref={ref}
                    size={size}
                    view={view}
                    options={options}
                    placeholder={placeholder}
                    filterable={suggested}
                    renderOption={renderOption}
                    className={cnDropdownQuestion('Control')}
                    defaultValue={showFirst && options[0] ? [options[0].value] : []}
                    label=""
                    onUpdate={onUpdate}
                    controlClassName={cnDropdownQuestion('InputControl')}
                    popupClassName={cnDropdownQuestion('Popup', {iframe})}
                    virtualizationThreshold={5}
                    disablePortal
                    error={Boolean(error)}
                />
            }
        />
    );
});
export default DropdownQuestion;
